import type { EntitiesByType, EntityWithMetadata } from '../../UI/dynamicUI/types';

type SelectMetadata = {
  type: string;
  options?: { value: string; label: string }[];
};

export const buildClientSummary = (entities: EntitiesByType): string => {
  const parts: (string | null)[] = [
    getClientName(entities.PersonalDetails),
    getClientAge(entities.PersonalDetails),
    getMaritalStatus(entities.PersonalDetails),
    getEmploymentStatus(entities.EmploymentInfo),
    getChildrenSummary(entities.Dependant),
    getTaxPayerRate(entities.TaxProfile),
    getRiskProfile(entities.RiskAndInvestmentPreferences),
  ];

  return parts.filter((part): part is string => part !== null).join(', ');
};

const calculateAge = (dateOfBirth: string): number => {
  const birth = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

const getSelectOptionLabel = (
  fieldValue: string | undefined,
  metadata: SelectMetadata | undefined,
): string | null => {
  if (!fieldValue || !metadata || metadata.type !== 'SELECT' || !metadata.options) {
    return null;
  }

  const option = metadata.options.find(opt => opt.value === fieldValue);
  return option ? option.label.toLowerCase() : null;
};

const getClientName = (
  personalDetails: EntityWithMetadata | EntityWithMetadata[] | undefined,
): string | null => {
  if (!personalDetails || Array.isArray(personalDetails)) {
    return null;
  }

  const fields = personalDetails.entity.fields;
  const firstName = fields.first_name as string | undefined;
  const lastName = fields.last_name as string | undefined;
  const name = [firstName, lastName].filter(Boolean).join(' ');

  return name || null;
};

const getClientAge = (
  personalDetails: EntityWithMetadata | EntityWithMetadata[] | undefined,
): string | null => {
  if (!personalDetails || Array.isArray(personalDetails)) {
    return null;
  }

  const dateOfBirth = personalDetails.entity.fields.date_of_birth as string | undefined;
  if (!dateOfBirth) {
    return null;
  }

  const age = calculateAge(dateOfBirth);
  return `${age} years old`;
};

const getMaritalStatus = (
  personalDetails: EntityWithMetadata | EntityWithMetadata[] | undefined,
): string | null => {
  if (!personalDetails || Array.isArray(personalDetails)) {
    return null;
  }

  const fields = personalDetails.entity.fields;
  const metadata = personalDetails.entity.metadata;
  const maritalStatus = fields.marital_status as string | undefined;

  return getSelectOptionLabel(maritalStatus, metadata.marital_status as SelectMetadata | undefined);
};

const getEmploymentStatus = (
  employmentInfo: EntityWithMetadata | EntityWithMetadata[] | undefined,
): string | null => {
  if (!employmentInfo || Array.isArray(employmentInfo)) {
    return null;
  }

  const fields = employmentInfo.entity.fields;
  const metadata = employmentInfo.entity.metadata;
  const employmentStatus = fields.employment_status as string | undefined;

  return getSelectOptionLabel(
    employmentStatus,
    metadata.employment_status as SelectMetadata | undefined,
  );
};

const getChildrenSummary = (
  dependants: EntityWithMetadata | EntityWithMetadata[] | undefined,
): string | null => {
  if (!Array.isArray(dependants)) {
    return null;
  }

  const children = dependants.filter(d => {
    const relationship = d.entity.fields.relationship as string | undefined;
    return relationship === 'Child';
  });

  if (children.length === 0) {
    return null;
  }

  const ages: number[] = [];
  for (const child of children) {
    const dateOfBirth = child.entity.fields.date_of_birth as string | undefined;
    if (dateOfBirth) {
      ages.push(calculateAge(dateOfBirth));
    }
  }

  const childWord = children.length === 1 ? 'child' : 'children';

  if (ages.length > 0) {
    return `has ${children.length} ${childWord} (ages ${ages.join(' and ')})`;
  }

  return `has ${children.length} ${childWord}`;
};

const getTaxPayerRate = (
  taxProfile: EntityWithMetadata | EntityWithMetadata[] | undefined,
): string | null => {
  if (!taxProfile || Array.isArray(taxProfile)) {
    return null;
  }

  const taxPayerRate = taxProfile.entity.fields.tax_payer_rate as string | undefined;
  if (!taxPayerRate) {
    return null;
  }

  return `${taxPayerRate} tax rate`;
};

const getRiskProfile = (
  riskPreferences: EntityWithMetadata | EntityWithMetadata[] | undefined,
): string | null => {
  if (!riskPreferences || Array.isArray(riskPreferences)) {
    return null;
  }

  const fields = riskPreferences.entity.fields;
  const metadata = riskPreferences.entity.metadata;
  const attitudeToRisk = fields.attitude_to_risk as string | undefined;

  return getSelectOptionLabel(
    attitudeToRisk,
    metadata.attitude_to_risk as SelectMetadata | undefined,
  );
};
